import { ArrowDownwardRounded, ArrowUpwardRounded, CloseRounded } from '@mui/icons-material';
import { Box, Chip, Drawer, IconButton, MenuItem, Select, Stack, Tooltip, Typography } from '@mui/material';
import {
  type FilterBarConfig,
  type FilterPlacement,
  defaultFilterBarConfig,
  moveFilter,
  orderedFilterBarItems,
} from '../lib/dashboardFilters';

const PLACEMENTS: Array<{ value: FilterPlacement; label: string; help: string }> = [
  { value: 'pinned', label: 'Pinned', help: 'Always visible on the filter bar.' },
  { value: 'more', label: 'More', help: 'Tucked behind "Add filter" until someone chooses it.' },
  { value: 'hidden', label: 'Hidden', help: 'Not offered at all on this dashboard.' },
];

/**
 * Filter-bar configuration, mirroring FLANK 2.0's drawer of the same name. A
 * dashboard built for one client rarely needs all ten controls, and the ones it
 * does need should lead — so each filter gets a placement and a position.
 *
 * Changes apply immediately and persist with the dashboard, matching how the
 * filters themselves already behave (every selection writes through).
 *
 * Admins can additionally publish the current set as the TENANT default (slice
 * F's middle layer). That is a ceiling, not a suggestion: a filter hidden at
 * tenant level is forced hidden on every dashboard and can't be re-enabled from
 * here — which is why the button says so before you press it.
 */
const FilterBarConfigDrawer = ({
  open,
  config,
  canManageTenant,
  tenantDefaults,
  tenantError,
  onClose,
  onChange,
  onSaveTenantDefault,
  onResetTenantDefault,
}: {
  open: boolean;
  config: FilterBarConfig;
  /** Whitelabel/org/super admins may set the default for the whole tenant. */
  canManageTenant: boolean;
  /** null = tenant has no opinion; [] = an admin disabled every filter. */
  tenantDefaults: string[] | null;
  tenantError: string | null;
  onClose: () => void;
  onChange: (next: FilterBarConfig) => void;
  onSaveTenantDefault: () => void;
  onResetTenantDefault: () => void;
}) => {
  const items = orderedFilterBarItems(config);
  const shownCount = items.filter(i => i.placement !== 'hidden').length;

  const setPlacement = (key: string, placement: FilterPlacement) =>
    onChange({ ...config, placement: { ...config.placement, [key]: placement } });

  return (
    <Drawer anchor='right' open={open} onClose={onClose} slotProps={{ paper: { sx: { width: 420 } } }}>
      <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant='h6' sx={{ flexGrow: 1 }}>
            Filter bar
          </Typography>
          <IconButton size='small' onClick={onClose} aria-label='Close filter bar settings'>
            <CloseRounded fontSize='small' />
          </IconButton>
        </Box>

        <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
          Choose which filters this dashboard offers and the order they appear in. Whatever is selected on the bar is
          saved as the dashboard's starting point.
          {canManageTenant && ' Changes affect this dashboard only unless you save them as the tenant default.'}
        </Typography>

        {tenantDefaults && (
          <Typography variant='caption' color='text.secondary' sx={{ mb: 1.5 }}>
            Your tenant default allows {tenantDefaults.length} filter
            {tenantDefaults.length === 1 ? '' : 's'} — anything outside it is locked to Hidden here.
          </Typography>
        )}

        {tenantError && (
          <Typography variant='caption' color='error' sx={{ mb: 1.5 }}>
            {tenantError}
          </Typography>
        )}

        <Chip
          size='small'
          variant='outlined'
          label={`${shownCount} of ${items.length} filters shown`}
          sx={{ alignSelf: 'flex-start', mb: 2 }}
        />

        <Stack sx={{ gap: 1, overflowY: 'auto', flexGrow: 1 }}>
          {items.map((item, index) => (
            <Box
              key={item.key}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1.5,
                opacity: item.placement === 'hidden' ? 0.55 : 1,
              }}
            >
              <Stack>
                <IconButton
                  size='small'
                  disabled={index === 0}
                  onClick={() => onChange(moveFilter(config, item.key, -1))}
                  aria-label={`Move ${item.label} up`}
                  sx={{ p: 0.25 }}
                >
                  <ArrowUpwardRounded sx={{ fontSize: 15 }} />
                </IconButton>
                <IconButton
                  size='small'
                  disabled={index === items.length - 1}
                  onClick={() => onChange(moveFilter(config, item.key, 1))}
                  aria-label={`Move ${item.label} down`}
                  sx={{ p: 0.25 }}
                >
                  <ArrowDownwardRounded sx={{ fontSize: 15 }} />
                </IconButton>
              </Stack>

              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant='body2' sx={{ fontWeight: 500 }}>
                  {item.label}
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  {item.key}
                </Typography>
              </Box>

              <Tooltip title={PLACEMENTS.find(p => p.value === item.placement)?.help ?? ''}>
                <Select
                  size='small'
                  value={item.placement}
                  onChange={e => setPlacement(item.key, e.target.value as FilterPlacement)}
                  sx={{ minWidth: 110 }}
                  inputProps={{ 'aria-label': `${item.label} placement` }}
                >
                  {PLACEMENTS.map(p => (
                    <MenuItem key={p.value} value={p.value}>
                      {p.label}
                    </MenuItem>
                  ))}
                </Select>
              </Tooltip>
            </Box>
          ))}
        </Stack>

        <Stack direction='row' sx={{ pt: 2, mt: 'auto', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label='Reset to default'
            variant='outlined'
            onClick={() => onChange(defaultFilterBarConfig())}
            sx={{ borderRadius: 1.5 }}
          />
          {canManageTenant && (
            <>
              <Tooltip title='Make this the starting set for every dashboard in your tenant. Filters set to Hidden here become unavailable tenant-wide.'>
                <Chip
                  label='Save as tenant default'
                  color='primary'
                  variant='outlined'
                  onClick={onSaveTenantDefault}
                  sx={{ borderRadius: 1.5 }}
                />
              </Tooltip>
              <Tooltip title='Remove the tenant default so dashboards fall back to the built-in set.'>
                <Chip
                  label='Clear tenant default'
                  variant='outlined'
                  onClick={onResetTenantDefault}
                  sx={{ borderRadius: 1.5 }}
                />
              </Tooltip>
            </>
          )}
        </Stack>
      </Box>
    </Drawer>
  );
};

export default FilterBarConfigDrawer;
